import { cva, type VariantProps } from 'class-variance-authority';
import { Alert02Icon, CancelCircleIcon, CheckmarkCircle02Icon, InformationCircleIcon, MinusSignCircleIcon, RecordIcon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const badgeVariants = cva(
  'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-0.5 text-meta font-medium [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-border-subtle bg-surface-subtle text-text-secondary',
        success: 'border-transparent bg-success-bg text-success',
        warning: 'border-transparent bg-warning-bg text-warning',
        danger: 'border-transparent bg-danger-bg text-danger',
        info: 'border-transparent bg-info-bg text-info',
        accent: 'border-transparent bg-accent-selected text-accent-on-selected',
        outline: 'border-border-subtle bg-surface-default text-text-secondary',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

/** Plain label chip (counts, tags). For states use StatusBadge, which always carries an icon + text. */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge({ className, tone, icon, children, ...props }, ref) {
  return (
    <span ref={ref} className={cn(badgeVariants({ tone }), className)} {...props}>
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
});

export type StatusTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

export const STATUS_ICONS: Record<StatusTone, React.ReactNode> = {
  neutral: <Icon icon={MinusSignCircleIcon} aria-hidden />,
  success: <Icon icon={CheckmarkCircle02Icon} aria-hidden />,
  warning: <Icon icon={Alert02Icon} aria-hidden />,
  danger: <Icon icon={CancelCircleIcon} aria-hidden />,
  info: <Icon icon={InformationCircleIcon} aria-hidden />,
  accent: <Icon icon={RecordIcon} aria-hidden />,
};

export interface StatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: StatusTone;
  /** Visible status text (required: status is never conveyed by color alone). */
  children: React.ReactNode;
  /** Override the default tone icon. Pass `false` only when the context gives another non-color cue. */
  icon?: React.ReactNode | false;
}

/** Semantic status: color + icon + text. */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge({ tone, icon, ...props }, ref) {
  return <Badge ref={ref} tone={tone} icon={icon === false ? undefined : (icon ?? STATUS_ICONS[tone])} {...props} />;
});
